import axios from 'axios';
import { unwrapObject, unwrapPaginated } from '../../recrutamento/api/envelope';

/**
 * Cliente do portal público de vagas.
 *
 * Instância **própria** de axios, sem o interceptor de token do app: o
 * candidato não tem sessão, e mandar um `Authorization` de outro usuário
 * logado na mesma máquina (ou disparar o fluxo de refresh) seria efeito
 * colateral indesejado numa área anônima.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

const publicApi = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export interface PublicJob {
  id: string;
  title: string;
  description: string;
  requirements?: string | null;
  city?: string | null;
  workModel?: 'presencial' | 'hibrido' | 'remoto' | null;
  jobType?: string | null;
  publishedAt?: string | null;
  deadline?: string | null;
}

export interface TrackedApplication {
  protocol: string;
  jobTitle?: string | null;
  stageKey: string;
  stageLabel: string;
  status: string;
  appliedAt: string;
  upcomingSchedules: Array<{
    stageKey: string;
    scheduledAt: string;
    location?: string | null;
    meetingLink?: string | null;
  }>;
}

export interface ApplyPayload {
  fullName: string;
  cpf: string;
  email: string;
  phone: string;
  city?: string;
  expectedSalary?: string;
  linkedinUrl?: string;
  consent: boolean;
  captchaToken?: string;
}

export const PORTAL_KEYS = {
  vagas: (params?: Record<string, string>) => ['portal-vagas', 'list', params] as const,
  vaga: (id: string) => ['portal-vagas', 'detail', id] as const,
};

export async function listarVagasPublicas(params?: {
  city?: string;
  workModel?: string;
  search?: string;
}): Promise<{ data: PublicJob[]; total: number }> {
  const { data } = await publicApi.get('/public/recrutamento/vagas', { params });
  const { data: vagas, total } = unwrapPaginated<PublicJob>(data);
  return { data: vagas, total };
}

export async function buscarVagaPublica(id: string): Promise<PublicJob> {
  const { data } = await publicApi.get(`/public/recrutamento/vagas/${id}`);
  return unwrapObject<PublicJob>(data);
}

/**
 * Inscrição. Vai como `multipart/form-data` porque leva o currículo —
 * e é por isso que `consent` viaja como string `"true"`, coerção que o
 * DTO do servidor conhece.
 */
export async function inscrever(
  jobId: string,
  payload: ApplyPayload,
  file: File,
): Promise<{ protocol: string; applicationId: string }> {
  const form = new FormData();
  form.append('fullName', payload.fullName);
  form.append('cpf', payload.cpf);
  form.append('email', payload.email);
  form.append('phone', payload.phone);
  form.append('consent', String(payload.consent));
  if (payload.city) form.append('city', payload.city);
  if (payload.expectedSalary) form.append('expectedSalary', payload.expectedSalary);
  if (payload.linkedinUrl) form.append('linkedinUrl', payload.linkedinUrl);
  if (payload.captchaToken) form.append('captchaToken', payload.captchaToken);
  form.append('file', file);

  const { data } = await publicApi.post(
    `/public/recrutamento/vagas/${jobId}/candidaturas`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrapObject(data);
}

export async function solicitarCodigoAcesso(email: string): Promise<{ requested: true }> {
  const { data } = await publicApi.post('/public/recrutamento/acompanhamento/token', { email });
  return unwrapObject(data);
}

export async function consultarAcompanhamento(
  email: string,
  token: string,
): Promise<{ applications: TrackedApplication[] }> {
  const { data } = await publicApi.post('/public/recrutamento/acompanhamento', { email, token });
  return unwrapObject(data);
}
